import { useCallback, useEffect, useRef, useState } from "react";
import { act } from "react-dom/test-utils";
import useEbsApiClient from "./useEbsApiClient.js";
import useTwitch from "./useTwitch.js";

export const FOLLOWER_STATUSES = {
  READY_FOR_CREATION: "READY_FOR_CREATION",
  CREATED: "CREATED",
  ALIVE: "ALIVE",
  DECEASED: "DECEASED",
};

export default function useFollowerRaffleLogic() {
  const ebsApiClient = useEbsApiClient();
  const {
    auth,
    bits,
    addPubSubEventListener,
    removePubSubEventListener,
    viewer,
  } = useTwitch();
  const [isRafflePermanentlyDismissed, setIsRafflePermanentlyDismissed] =
    useState(false);
  const [activeRaffle, setActiveRaffle] = useState(null);
  const [follower, setFollower] = useState(null);
  const followerRef = useRef(follower);
  const [channelInfo, setChannelInfo] = useState(null);

  const fetchViewerFollower = async () => {
    try {
      const { data: viewerFollower } = await ebsApiClient.get("/followers/me");
      setFollower(viewerFollower);
    } catch (err) {
      setFollower(null);
    }
  };

  const dismissRaffle = () => {
    setActiveRaffle(null);
  };

  const permanentlyDismissRaffle = () => {
    setIsRafflePermanentlyDismissed(true);
  };

  const enterRaffle = async () => {
    try {
      setActiveRaffle((raffle) => ({ ...raffle, participated: true }));
      await ebsApiClient.post("/followers/raffle/participate");
    } catch (err) {
      setActiveRaffle((raffle) => ({ ...raffle, participated: false }));
    }
  };

  const createFollower = async ({ skinName, color, premium }) => {
    let transaction = null;

    if (premium) {
      console.log("Premium skin, requesting Bits from user");
      try {
        transaction = await bits.useBitsPromisified(
          channelInfo.premium_skins_bits_sku
        );
      } catch (err) {
        console.log(err);
        console.log("TODO: handle error");
      }
    }

    if (premium && !transaction) {
      // Bailing early since something went wrong with the transaction
      return;
    }

    try {
      await ebsApiClient.post("/followers/me", {
        skin_name: skinName,
        color,
        transaction,
        save_id: follower.save_id,
      });
      setFollower((follower) => ({
        ...follower,
        status: FOLLOWER_STATUSES.CREATED,
      }));
    } catch (err) {}
  };

  const fetchRaffle = async () => {
    try {
      const response = await ebsApiClient.get("/followers/raffle");
      setActiveRaffle(response.data);
    } catch (err) {}
  };

  const handleRaffleStart = async (data) => {
    /**
     * Inspect if save ID in data matches the follower's save ID - if not,
     * first re-fetch the follower. In that case also reset perma dismiss status
     * to show the popup again because the viewer CAN create a follower for
     * this save.
     */
    if (followerRef.current && data.save_id !== followerRef.current.save_id) {
      await fetchViewerFollower();
      setIsRafflePermanentlyDismissed(false);
    }

    setActiveRaffle(data);
  };

  const handleRaffleEnd = (data) => {
    // TODO: replace this by fetching the viewer's follower if they win
    if (data.winning_viewer_id === viewer.id) {
      setFollower({
        status: FOLLOWER_STATUSES.READY_FOR_CREATION,
        save_id: data.save_id,
      });
    }

    // TOOD: expose the 'win/lose' status to the hook consumer and don't clear the raffle right away?
    setActiveRaffle(null);
  };

  useEffect(() => {
    followerRef.current = follower;
  }, [followerRef, follower]);

  useEffect(() => {
    (async () => {
      try {
        const { data: channelData } = await ebsApiClient.get("/channel");
        setChannelInfo(channelData);
        fetchViewerFollower();
      } catch (err) {
        // No follower found, silent fail
      }
    })();
  }, []);

  useEffect(() => {
    addPubSubEventListener("cotl.raffle.start", handleRaffleStart);
    addPubSubEventListener("cotl.raffle.end", handleRaffleEnd);

    fetchRaffle();

    return () => {
      removePubSubEventListener("cotl.raffle.start", handleRaffleStart);
      removePubSubEventListener("cotl.raffle.end", handleRaffleEnd);
    };
  }, []);

  return {
    activeRaffle: isRafflePermanentlyDismissed ? null : activeRaffle,
    follower,
    enterRaffle,
    dismissRaffle,
    permanentlyDismissRaffle,
    createFollower,
    premiumBitsCost: Boolean(channelInfo?.premium_skins_bits_sku)
      ? Number(channelInfo?.premium_skins_bits_sku.split("_")[2])
      : null,
  };
}
